// junip-item-video.ts

// Define the custom element class
export class JunipItemVideo extends HTMLElement {
  // Shadow DOM for encapsulation
  private shadow: ShadowRoot;
  private imgSrc: string;
  private imgAlt: string;
  private videoSrc: string;

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });

    // Get attributes or set defaults
    this.imgSrc = this.getAttribute('src') || 'https://via.placeholder.com/150';
    this.imgAlt = this.getAttribute('alt') || 'Item Image';
    this.videoSrc = this.getAttribute('video-src') || '';

    // Render the component
    this.render();
  }

  // Render the HTML and CSS
  private render(): void {
    this.shadow.innerHTML = `
      <style>
        :host {
          display: block;
          cursor: pointer;
        }
        .thumbnail {
          width: 150px;
          height: 150px;
          object-fit: cover;
          border-radius: 8px;
        }
        .modal {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal.open {
          display: flex;
        }
        .modal-content {
          background-color: white;
          padding: 20px;
          border-radius: 8px;
          position: relative;
          max-width: 80%;
          max-height: 80%;
          overflow: auto;
        }
        .modal-video {
          max-width: 100%;
          max-height: 60vh;
          display: block;
        }
        .close-button {
          position: absolute;
          top: 10px;
          right: 10px;
          background: #ff4d4d;
          color: white;
          border: none;
          border-radius: 50%;
          width: 30px;
          height: 30px;
          font-size: 16px;
          cursor: pointer;
          line-height: 30px;
          text-align: center;
        }
        .close-button:hover {
          background: #cc0000;
        }
      </style>
      <img class="thumbnail" src="${this.imgSrc}" alt="${this.imgAlt}" />
      <div class="modal">
        <div class="modal-content">
          ${
            this.videoSrc
              ? `<video class="modal-video" controls>
                   <source src="${this.videoSrc}" type="video/mp4">
                   Your browser does not support the video tag.
                 </video>`
              : `<p>No video source provided.</p>`
          }
          <button class="close-button">✕</button>
        </div>
      </div>
    `;

    // Add event listeners
    this.addEventListeners();
  }

  // Set up event listeners for interactivity
  private addEventListeners(): void {
    const thumbnail = this.shadow.querySelector('.thumbnail') as HTMLImageElement;
    const modal = this.shadow.querySelector('.modal') as HTMLDivElement;
    const closeButton = this.shadow.querySelector('.close-button') as HTMLButtonElement;
    // const modalContent = this.shadow.querySelector('.modal-content') as HTMLDivElement;

    // Open modal on thumbnail click
    thumbnail.addEventListener('click', () => {
      modal.classList.add('open');
      // Play video if available
      const video = this.shadow.querySelector('.modal-video') as HTMLVideoElement | null;
      if (video) {
        video.play().catch((error) => console.error('Video playback failed:', error));
      }
    });

    // Close modal on close button click
    closeButton.addEventListener('click', () => {
      modal.classList.remove('open');
      // Pause video if available
      const video = this.shadow.querySelector('.modal-video') as HTMLVideoElement | null;
      if (video) {
        video.pause();
      }
    });

    // Close modal when clicking outside the modal content
    modal.addEventListener('click', (event: MouseEvent) => {
      if (event.target === modal) {
        modal.classList.remove('open');
        // Pause video if available
        const video = this.shadow.querySelector('.modal-video') as HTMLVideoElement | null;
        if (video) {
          video.pause();
        }
      }
    });
  }

  // Observe attribute changes
  static get observedAttributes(): string[] {
    return ['src', 'alt', 'video-src'];
  }

  // Handle attribute changes
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue !== newValue) {
      if (name === 'src') {
        this.imgSrc = newValue;
      } else if (name === 'alt') {
        this.imgAlt = newValue;
      } else if (name === 'video-src') {
        this.videoSrc = newValue;
      }
      this.render();
    }
  }
}
